/**
 * Mount Keyframe Context Overlay
 *
 * Shows a viewport overlay when the user is working inside a Keyframes container.
 * The overlay hosts the keyframe card strip — Base + a thumbnail card per
 * keyframe — for selecting, authoring, renaming, and previewing keyframes.
 * (The strip used to live in the behavior panel; it now lives here so the
 * thumbnails sit over the viewport they depict.) Persists when navigating
 * between children of the container.
 *
 * There is no separate "edit mode" (spec: docs-internal/KEYFRAME_EDITOR_STATES.md):
 * when a keyframe is selected, edits under the container are stored in it —
 * the overlay tints blue to signal that. At Base, edits change the scene.
 *
 * Similar to mount-page-context.tsx but for the Keyframes system.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { observeInspectorPaneInsets } from './inspector-panes';
import type { SceneObject } from 'trace-viewer/scene';
import type { Keyframes, KeyframesData, KeyframeDefinition } from 'trace-viewer/behaviors';
import { getUndoManager, BehaviorCommitCommand, defaultStateTransition } from 'trace-viewer/editor';
import { EDITOR_EVENTS } from './editor-events';
import { PoseStrip, type PoseAdapter, type PoseThumbnailProvider } from './pose-strip';
import { captureSubtreePoseEntries } from './keyframe-capture';
import {
    BASE_STATE,
    captureKeyframeThumbnail,
    duplicateKeyframeThumbnail,
    generateMissingThumbnails,
    getBaseSubtreeSignature,
    getKeyframeThumbnail,
    invalidateKeyframeThumbnail,
    onKeyframeThumbnailsChanged,
    renameKeyframeThumbnail,
} from './keyframe-thumbnails';

// ── Types ────────────────────────────────────────────────────

export interface KeyframeContext {
    container: SceneObject;
    keyframes: Keyframes;
    activeKeyframe: string | null;
    keyframeNames: string[];
}

/** Look up a Keyframes behavior by string name. */
function getKeyframes(obj: SceneObject): Keyframes | null {
    return obj.getBehavior('Keyframes') as Keyframes | null;
}

/**
 * Resolve the Keyframes context for a given selection.
 *
 * Walks up from the selected object (inclusive). A container with a SELECTED
 * keyframe wins over a nearer container at Base — this mirrors
 * `getKeyframeCaptureContext` (keyframe-capture.ts), which routes edits past
 * Base containers into the nearest capturing ancestor. The overlay must agree
 * with capture routing, or it would read "Base / edits change the scene"
 * while a drag actually lands in an outer container's keyframe.
 */
export function resolveKeyframeContext(selected: SceneObject): KeyframeContext | null {
    let nearest: KeyframeContext | null = null;
    let current: SceneObject | null = selected;
    while (current) {
        const kf = getKeyframes(current);
        if (kf) {
            const ctx: KeyframeContext = {
                container: current,
                keyframes: kf,
                activeKeyframe: kf.getActiveKeyframe(),
                keyframeNames: kf.getKeyframeNames(),
            };
            if (ctx.activeKeyframe !== null) return ctx; // capturing container wins
            nearest ??= ctx;
        }
        current = current.getParent();
    }
    return nearest;
}

/**
 * Commit a `KeyframesData` change from the overlay and record an undo entry.
 *
 * The overlay mounts its own React root outside the behavior panel, so it can't
 * reuse the panel's `liveCommit`. The card-strip operations (add/delete/rename a
 * keyframe, transition defaults, initial keyframe) are plain `KeyframesData`
 * edits — not Properties edits — so a straight commit + BehaviorCommitCommand is
 * the right undo unit (no keyframe-capture routing involved).
 *
 * Returns true on success. Callers that have thumbnail-cache side effects to run
 * (rename/delete) gate them on this so a rejected commit doesn't orphan a cache
 * entry against data that never changed.
 */
async function commitKeyframesData(behavior: Keyframes, next: KeyframesData): Promise<boolean> {
    const before = behavior.getData();
    const errors = await behavior.commit(next);
    if (errors.length > 0) {
        console.warn('[KeyframeOverlay] commit rejected:', errors.map((e) => e.message).join(', '));
        return false;
    }
    const after = behavior.getData();
    if (!getUndoManager().isUndoRedoing) {
        getUndoManager().push(new BehaviorCommitCommand(
            behavior.sceneObject,
            'Keyframes',
            before,
            after,
            null,
        ));
    }
    return true;
}

// ── Keyframe selection notification ──────────────────────────

/**
 * Tell the behaviors panel to re-read runtime state after a keyframe selection
 * changes, so its open sections reflect the newly-applied (or cleared) keyframe.
 *
 * We deliberately do NOT dispatch a `Properties`/`updated` BEHAVIOR_CHANGED for
 * each descendant: that event is also consumed by the always-on keyframe capture
 * listener (`_onBehaviorChanged` in keyframe-capture.ts), which would call
 * `capturePropertyEditIntoKeyframe` for every descendant and — when an object
 * has no capture baseline yet — spuriously bake its current Base declarations
 * into the just-selected keyframe. Instead we fire one selection-refresh event
 * the panel listens for but the capture listener ignores.
 */
function notifyKeyframeSelectionChanged(behavior: Keyframes): void {
    window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.KEYFRAME_SELECTION_CHANGED, {
        detail: { containerPath: behavior.sceneObject.getPath() },
    }));
}

// ── Card strip component ─────────────────────────────────────

interface KeyframeStripProps {
    keyframes: Keyframes;
    containerName: string;
}


function KeyframeStrip({ keyframes: behavior, containerName: initialName }: KeyframeStripProps) {
    const [data, setData] = useState<KeyframesData>(() => behavior.getData());
    const [activeKeyframe, setActiveKeyframe] = useState<string | null>(behavior.getActiveKeyframe());
    // Container display name, polled below so a rename-in-place is reflected
    // without depending on the mount layer re-rendering us (it only re-renders on
    // container switch / behavior add-remove, not on rename). Seeded from the prop.
    const [containerName, setContainerName] = useState(initialName);
    // Signature of the Base pose of the captured subtree. A Base edit (e.g.
    // dragging a descendant while at Base) changes nothing in KeyframesData, so
    // the data poll can't see it — but it restyles the Base thumbnail AND every
    // keyframe (base-inheritance: a keyframe is a diff from Base). We poll this
    // separately to trigger a regen when it moves.
    const [baseSig, setBaseSig] = useState<string>(() => getBaseSubtreeSignature(behavior));
    // Tracks each keyframe's last-seen definition JSON so we can invalidate the
    // thumbnail of any keyframe whose entries changed (PoseStrip re-renders on the
    // resulting cache-change event). The card UI (hover/rename/menu) is owned by
    // PoseStrip — this component only runs the keyframe-specific thumbnail
    // lifecycle and feeds PoseStrip an adapter.
    const prevDefsRef = useRef<Map<string, string>>(new Map());

    const keyframeNames = Object.keys(data.keyframes ?? {});

    // Poll the behavior for the selected keyframe AND the latest authored data
    // (the runtime is the source of truth — navigation and edits can also come
    // from scripts, undo/redo, or the property panel). Cheap: getData() returns
    // a clone and we only setState when the JSON actually changes. Seed the ref
    // from the same snapshot `data` was initialized with, so the first poll
    // doesn't fire a redundant setData for data that hasn't changed.
    const dataJsonRef = useRef<string | undefined>(undefined);
    if (dataJsonRef.current === undefined) dataJsonRef.current = JSON.stringify(data);
    useEffect(() => {
        const poll = () => {
            setActiveKeyframe(behavior.getActiveKeyframe());
            setContainerName(behavior.sceneObject.name); // reflect rename-in-place
            const next = behavior.getData();
            const json = JSON.stringify(next);
            if (json !== dataJsonRef.current) {
                dataJsonRef.current = json;
                setData(next);
            }
            // Only meaningful at Base — while a keyframe is applied the signature
            // reads authored poses (keyframe-independent), so it stays put and we
            // don't churn. setBaseSig bails early when unchanged (same string).
            setBaseSig(getBaseSubtreeSignature(behavior));
        };
        poll();
        const id = setInterval(poll, 300);
        return () => clearInterval(id);
    }, [behavior]);

    // (PoseStrip subscribes to thumbnail-cache changes and re-renders itself.)

    // Invalidate thumbnails of keyframes whose definition changed (undo/redo,
    // panel edits). The selected keyframe is skipped — the debounced capture
    // below refreshes it in place, avoiding a placeholder flash mid-edit.
    useEffect(() => {
        const next = new Map<string, string>();
        for (const [name, def] of Object.entries(data.keyframes ?? {})) {
            const json = JSON.stringify(def);
            next.set(name, json);
            const prev = prevDefsRef.current.get(name);
            if (prev !== undefined && prev !== json && name !== behavior.getActiveKeyframe()) {
                invalidateKeyframeThumbnail(behavior, name);
            }
        }
        prevDefsRef.current = next;
    }, [behavior, data]);

    // Debounced live capture of whatever state is currently selected (Base when
    // null). Fires after navigation transitions settle and after edits. Skips
    // when the cached thumbnail is already fresh (dedup in the cache module).
    const dataJson = useMemo(() => JSON.stringify(data), [data]);
    const transitionSec = (activeKeyframe ? data.keyframes[activeKeyframe]?.transition?.duration : undefined)
        ?? data.transitionDuration ?? 0.5;
    useEffect(() => {
        const target = activeKeyframe;
        const delay = Math.max(900, transitionSec * 1000 + 300);
        const timer = setTimeout(() => {
            if (behavior.getActiveKeyframe() !== target) return;
            void captureKeyframeThumbnail(behavior, target);
        }, delay);
        return () => clearTimeout(timer);
    }, [behavior, activeKeyframe, dataJson, transitionSec]);

    // Proactive fill for unvisited keyframes + Base, posed off-screen (no live
    // navigation — see generateMissingThumbnails). Re-runs when the keyframe set
    // changes so newly-added keyframes get filled, and when the Base pose changes
    // — a Base edit restyles the Base card AND every keyframe (base-inheritance),
    // which the new content hash now reflects, so this regen recaptures exactly
    // the stale cards (already-fresh ones are skipped by the cache dedup).
    // NUL-join so names with spaces can't collide into one key.
    const keyframeSetKey = useMemo(() => keyframeNames.join('\0'), [keyframeNames]);
    useEffect(() => {
        const ac = new AbortController();
        const timer = setTimeout(() => {
            void generateMissingThumbnails(behavior, ac.signal).catch(() => {});
        }, 300);
        return () => { ac.abort(); clearTimeout(timer); };
    }, [behavior, keyframeSetKey, baseSig]);

    /**
     * Select a keyframe as soon as the runtime knows about it. A plain goTo
     * right after commit races the async commit: the resolved data may not
     * contain the new keyframe yet, in which case goTo silently no-ops. Retry
     * on a timer until the commit lands, abort if the user navigates meanwhile.
     */
    const selectWhenReady = useCallback((name: string, attempts = 40) => {
        if (behavior.getKeyframeNames().includes(name)) {
            void behavior.goTo(name, false);
            setActiveKeyframe(name);
            notifyKeyframeSelectionChanged(behavior);
            return;
        }
        if (attempts <= 0) return;
        const activeAtSchedule = behavior.getActiveKeyframe();
        setTimeout(() => {
            if (behavior.getActiveKeyframe() !== activeAtSchedule) return; // user navigated
            selectWhenReady(name, attempts - 1);
        }, 50);
    }, [behavior]);

    // Card clicks ANIMATE the viewport transition (animate=true) — the transition
    // preview is a wanted authoring feature. The behaviors PANEL, by contrast,
    // snaps straight to the keyframe's stored values (seeded via
    // overlayKeyframeValues from the authored entry, not the mid-transition
    // interpolation) so its fields never jitter or fight edits while the viewport
    // animates.
    const selectBase = useCallback(() => {
        void behavior.goTo(BASE_STATE, true);
        setActiveKeyframe(null); // optimistic; the poll confirms
        notifyKeyframeSelectionChanged(behavior);
    }, [behavior]);

    const handleKeyframeClick = useCallback((name: string) => {
        if (activeKeyframe === name) return; // re-click is a no-op; Base has its own card
        void behavior.goTo(name, true);
        setActiveKeyframe(name); // optimistic; the poll confirms
        notifyKeyframeSelectionChanged(behavior);
    }, [behavior, activeKeyframe]);

    const addKeyframe = useCallback(() => {
        const existing = new Set(keyframeNames);
        let n = existing.size + 1;
        let name = `Keyframe ${n}`;
        while (existing.has(name)) {
            n += 1;
            name = `Keyframe ${n}`;
        }
        const next: KeyframesData = {
            ...data,
            keyframes: {
                ...(data.keyframes ?? {}),
                [name]: {
                    // Give new keyframes a default transition so switching to them
                    // animates out of the box — the SAME default states use, so the
                    // two stay in sync from one source. Per-entry transitions can
                    // still override this later.
                    transition: defaultStateTransition(),
                    // Key the current ("active") subtree pose so the keyframe captures
                    // how things look right now instead of starting empty.
                    entries: captureSubtreePoseEntries(behavior.sceneObject),
                },
            },
        };
        // Don't auto-assign initialKeyframe — leave it unset (Base) until the
        // user explicitly picks an initial keyframe in the editor.
        // Gate the select on commit success (like delete/duplicate) — on a
        // rejected commit the keyframe never lands, and an unconditional
        // selectWhenReady would poll for ~2s before giving up.
        void commitKeyframesData(behavior, next).then((ok) => {
            if (ok) selectWhenReady(name);
        });
    }, [data, keyframeNames, behavior, selectWhenReady]);

    const duplicateKeyframe = useCallback((name: string) => {
        const source = data.keyframes?.[name];
        if (!source) return;
        // Pick a unique "<name> copy", "<name> copy 2", … name.
        const existing = new Set(keyframeNames);
        let copyName = `${name} copy`;
        let n = 2;
        while (existing.has(copyName)) {
            copyName = `${name} copy ${n}`;
            n += 1;
        }
        // Insert the duplicate right after its source so it reads as a sibling.
        const nextKeyframes: Record<string, KeyframeDefinition> = {};
        for (const [k, v] of Object.entries(data.keyframes ?? {})) {
            nextKeyframes[k] = v;
            if (k === name) nextKeyframes[copyName] = structuredClone(source);
        }
        const next: KeyframesData = { ...data, keyframes: nextKeyframes };
        // The copy looks identical to its source, so seed its thumbnail from the
        // source for instant feedback (the off-screen fill would otherwise leave
        // a placeholder until it gets around to it). Only after the commit lands.
        void commitKeyframesData(behavior, next).then((ok) => {
            if (!ok) return;
            duplicateKeyframeThumbnail(behavior, name, copyName);
            selectWhenReady(copyName);
        });
    }, [data, keyframeNames, behavior, selectWhenReady]);

    const deleteKeyframeByName = useCallback((name: string) => {
        const nextKeyframes = { ...(data.keyframes ?? {}) };
        delete nextKeyframes[name];
        const next: KeyframesData = { ...data, keyframes: nextKeyframes };
        // If the deleted keyframe was the chosen initial, clear it back to Base
        // rather than auto-picking another — initialKeyframe stays unset until
        // the user explicitly chooses one.
        if (data.initialKeyframe === name) {
            next.initialKeyframe = undefined;
        }
        // Gate ALL side-effects on the commit landing — a rejected commit must not
        // leave the runtime deselected (at Base) while the keyframe still exists,
        // nor drop the cached thumbnail for a card that's still present. Deselect
        // here (after the commit) so the deleted keyframe's contributions clear.
        void commitKeyframesData(behavior, next).then((ok) => {
            if (!ok) return;
            if (behavior.getActiveKeyframe() === name) {
                void behavior.goTo(null, false);
                setActiveKeyframe(null);
            }
            invalidateKeyframeThumbnail(behavior, name);
        });
    }, [data, behavior]);

    // Rename by name (PoseStrip owns the inline-edit UI + draft). Preserves the
    // keyframe insertion order, moves the cached thumbnail, and re-selects under
    // the new name — all gated on commit success (a rejected commit leaves the
    // old name live, so eager side-effects would orphan the thumbnail).
    const renameKeyframeByName = useCallback((oldName: string, newName: string) => {
        const names = Object.keys(behavior.getData().keyframes ?? {});
        if (!newName || newName === oldName || names.includes(newName)) return;
        const cur = behavior.getData();
        const nextKeyframes: Record<string, KeyframeDefinition> = {};
        for (const [k, v] of Object.entries(cur.keyframes ?? {})) {
            nextKeyframes[k === oldName ? newName : k] = v;
        }
        const next: KeyframesData = { ...cur, keyframes: nextKeyframes };
        if (cur.initialKeyframe === oldName) next.initialKeyframe = newName;
        const wasSelected = behavior.getActiveKeyframe() === oldName;
        void commitKeyframesData(behavior, next).then((ok) => {
            if (!ok) return;
            renameKeyframeThumbnail(behavior, oldName, newName);
            if (wasSelected) selectWhenReady(newName);
        });
    }, [behavior, selectWhenReady]);

    // Build the thumbnail provider from the keyframe cache (wraps the existing
    // module). Memoized on the behavior so PoseStrip's subscribe effect is stable.
    const thumbnails: PoseThumbnailProvider = useMemo(() => ({
        getThumbnail: (name: string | null) => getKeyframeThumbnail(behavior, name === null ? BASE_STATE : name),
        subscribe: (cb: () => void) => onKeyframeThumbnailsChanged(cb),
    }), [behavior]);

    // Adapt the keyframe actions to the generic PoseStrip. select(null) = Base.
    const adapter: PoseAdapter = useMemo(() => ({
        owner: behavior.sceneObject,
        ownerName: containerName,
        noun: 'keyframe',
        getNames: () => Object.keys(behavior.getData().keyframes ?? {}),
        getActive: () => behavior.getActiveKeyframe(),
        select: (name, animate) => {
            if (name === null) selectBase();
            else handleKeyframeClick(name);
            void animate;
        },
        add: () => addKeyframe(),
        remove: (name) => deleteKeyframeByName(name),
        rename: (oldName, newName) => renameKeyframeByName(oldName, newName),
        duplicate: (name) => duplicateKeyframe(name),
        thumbnails,
    }), [behavior, containerName, thumbnails, selectBase, handleKeyframeClick, addKeyframe, deleteKeyframeByName, renameKeyframeByName, duplicateKeyframe]);

    return <PoseStrip adapter={adapter} />;
}

// ── Mount logic ──────────────────────────────────────────────

export interface KeyframeContextControls {
    update: (selectedObject: SceneObject | null) => void;
    dispose: () => void;
}

export function mountKeyframeContext(): KeyframeContextControls {
    const containerEl = document.getElementById('keyframe-context-container');
    if (!containerEl) {
        return { update() {}, dispose() {} };
    }
    // Re-bind after null guard so TS narrows the type inside closures.
    const el = containerEl;

    let root: Root | null = null;
    let disposePaneObserver: (() => void) | null = null;
    let lastContainer: SceneObject | null = null;

    function setupPaneObserver() {
        disposePaneObserver?.();
        disposePaneObserver = observeInspectorPaneInsets(({ left, right }) => {
            el.style.left = `${left}px`;
            el.style.right = `${right}px`;
        });
    }

    function render(ctx: KeyframeContext | null) {
        root ??= createRoot(el);
        if (!ctx) {
            root.render(null);
            return;
        }
        // Key on the container so the strip's internal state resets when the
        // user moves to a different Keyframes container.
        root.render(
            <KeyframeStrip
                key={ctx.container.transform.uniqueId}
                keyframes={ctx.keyframes}
                containerName={ctx.container.name}
            />,
        );
    }

    function update(selected: SceneObject | null) {
        const ctx = selected ? resolveKeyframeContext(selected) : null;
        const container = ctx?.container ?? null;
        // Only re-render the React tree when the resolved container changes. The
        // strip polls the behavior itself for selection/data/name (so a rename in
        // place is reflected without a re-render here), so we don't need to
        // re-render on every selection change within the same container.
        if (container !== lastContainer) {
            lastContainer = container;
            render(ctx);
        }
        requestAnimationFrame(() => setupPaneObserver());
    }

    return {
        update,
        dispose: () => {
            disposePaneObserver?.();
            disposePaneObserver = null;
            root?.unmount();
            root = null;
            lastContainer = null;
        },
    };
}
